/**
 * `userDataExt.ts` — port of Unity's `Models/Data/UserDataExt.cs` and the
 * parse half of `AwsAdminStorageModel.GetUserData` (`:1200-1292`).
 *
 * This is the account-side profile of a person: the LICENCE role their admin
 * assigned them, whether they may present, their kinematics seat and their
 * trial window. It is the single piece of data the whole permissions port was
 * blocked on — nothing in the web client had ever fetched it.
 *
 * SOURCE (verified verbatim at `AwsAdminStorageModel.cs:256-280`):
 *   { getAccountPersonByUsername(accountid: "…", username: "…") {
 *       appRole username firstName lastName email personid tutorialStatus
 *       hasKinematicsSeat canBePresenter trialEnabled trialEndDate
 *       trialStartDate preTrialRole } }
 * Same AppSync endpoint and same raw-ID-token `authorization` header the web
 * already uses for `getSharedLinksPipeline` — so this was unported work on a
 * working transport, not an unreachable API.
 */

import { ACCOUNT_ROLE } from "./sharedLinkDto";
import type { AccountRole } from "./userRolePermissions";

export interface UserDataExt {
  readonly userId: string;
  readonly personId: string;
  /** The role assigned in the user's NATURAL account. Defaults to Reader. */
  readonly accountRole: AccountRole;
  /**
   * Admin-portal flag, meaningful mainly for users WITHOUT a core licence —
   * a Writer is forced presenter-eligible below. Defaults false.
   */
  readonly canBePresenter: boolean;
  readonly hasKinematicsSeat: boolean;
  readonly isTrialUser: boolean;
  /** Epoch ms, or null when absent / not on trial. */
  readonly trialStartDate: number | null;
  readonly trialEndDate: number | null;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly tutorialStatus: string;
}

/** Everything defaults to the LEAST privilege — see `parseUserDataExt`. */
export function emptyUserDataExt(userId: string): UserDataExt {
  return {
    userId,
    personId: "",
    accountRole: ACCOUNT_ROLE.Reader,
    canBePresenter: false,
    hasKinematicsSeat: false,
    isTrialUser: false,
    trialStartDate: null,
    trialEndDate: null,
    firstName: "",
    lastName: "",
    email: "",
    tutorialStatus: "",
  };
}

/**
 * `appRole` string → `AccountRole`, with the TWO forcing side effects that
 * are easy to miss (`AwsAdminStorageModel.cs:1264-1285`):
 *   - `"Writer"` ALSO forces `canBePresenter = true` — a core-licence holder
 *     may always present regardless of the admin flag.
 *   - the obsolete `"Presenter"` maps the role to **Commenter** and ALSO
 *     forces `canBePresenter = true`. It does NOT map to `ACCOUNT_ROLE.Presenter`.
 * An unrecognised or absent `appRole` leaves the seeded Reader in place.
 */
function applyAppRole(
  appRole: string,
  seed: { accountRole: AccountRole; canBePresenter: boolean },
): void {
  switch (appRole) {
    case "Writer":
      seed.accountRole = ACCOUNT_ROLE.Writer;
      seed.canBePresenter = true;
      break;
    case "Reader":
      seed.accountRole = ACCOUNT_ROLE.Reader;
      break;
    case "Commenter":
      seed.accountRole = ACCOUNT_ROLE.Commenter;
      break;
    case "Presenter":
      seed.accountRole = ACCOUNT_ROLE.Commenter;
      seed.canBePresenter = true;
      break;
    default:
      break;
  }
}

/**
 * Parse one `getAccountPersonByUsername` row.
 *
 * ⚠ UPSTREAM BUG DELIBERATELY NOT PORTED — the expired-trial field mismatch.
 * Unity's query selects **`preTrialRole`** (`:274`) but the expired-trial
 * branch reads **`accountData["preTrialAccountRole"]`** (`:1257`), which is
 * absent → `.Value<string>()` on a null token throws → the exception is
 * swallowed by the outer catch at `:1285` → the whole parse is abandoned and
 * the user silently falls back to **Reader**, losing their name and personId
 * too. So on desktop an expired-trial user is demoted by an exception rather
 * than by policy. We read `preTrialRole` (the field actually selected), and
 * fall back to `preTrialAccountRole` only if some deployment really returns
 * that spelling.
 */
export function parseUserDataExt(userId: string, row: Record<string, unknown> | null): UserDataExt {
  const seed = {
    accountRole: ACCOUNT_ROLE.Reader as AccountRole,
    canBePresenter: false,
  };
  if (!row) return emptyUserDataExt(userId);

  const isTrialUser = row.trialEnabled === true;
  const trialStartDate = epochMs(row.trialStartDate);
  const trialEndDate = epochMs(row.trialEndDate);
  let hasKinematicsSeat = row.hasKinematicsSeat === true;
  if (typeof row.canBePresenter === "boolean") seed.canBePresenter = row.canBePresenter;

  let appRole = typeof row.appRole === "string" ? row.appRole : "";

  // An ENDED trial reverts the user to their pre-trial role and drops the
  // kinematics seat (`:1252-1262`). Date-only comparison, like Unity's
  // `TrialEndDate.Date < DateTime.Today`.
  if (isTrialUser && trialEndDate !== null && trialStartDate !== null) {
    const endDay = startOfUtcDay(trialEndDate);
    const today = startOfUtcDay(Date.now());
    if (endDay < today) {
      const preTrial = typeof row.preTrialRole === "string"
        ? row.preTrialRole
        : (typeof row.preTrialAccountRole === "string" ? row.preTrialAccountRole : "");
      // Only override when the server actually told us what to revert TO.
      // Upstream throws here; falling back to the live `appRole` keeps the
      // user at whatever the account currently says rather than demoting
      // them to Reader by accident.
      if (preTrial) appRole = preTrial;
      hasKinematicsSeat = false;
    }
  }

  applyAppRole(appRole, seed);

  return {
    userId,
    personId: str(row.personid),
    accountRole: seed.accountRole,
    canBePresenter: seed.canBePresenter,
    hasKinematicsSeat,
    isTrialUser,
    trialStartDate,
    trialEndDate,
    firstName: str(row.firstName),
    lastName: str(row.lastName),
    email: str(row.email),
    tutorialStatus: str(row.tutorialStatus),
  };
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** The trial dates arrive as epoch MILLISECONDS (Unity `TimeUtils.ToDateTime`). */
function epochMs(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
    const date = Date.parse(value);
    if (Number.isFinite(date)) return date;
  }
  return null;
}

function startOfUtcDay(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
